import React from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import ButtonSlim from '../../../../../../web-app-components/components/ButtonSlim';
import makeStyles from '@material-ui/core/styles/makeStyles';
import RemoveIcon from '@material-ui/icons/DeleteOutline';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import ButtonFilePicker from '../../../../../../web-app-components/components/ButtonFilePicker';
import Box from '@material-ui/core/Box';

const useStyles = makeStyles((theme) => ({
  fileContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    '& > $fileActions': {
      display: 'none',
    },
    '&:hover': {
      '& > $fileActions': {
        display: 'initial',
      },
    },
  },
  fileDisplay: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  fileIcon: {
    marginRight: theme.spacing(2),
    fontSize: 48,
    color: theme.palette.text.secondary,
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  fileSize: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  fileActions: {
    position: 'absolute',
    left: 0,
    top: 0,
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
  uploadButton: {
    marginBottom: theme.spacing(1),
  },
}));

const GXPFileField = ({ name, label, disabled, ...props }) => {
  const classes = useStyles();
  const [{ value }, { error, touched }, { setValue }] = useField(name);

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      // ButtonFilePicker passes an array, take the first file
      const file = files[0];
      setValue(file);
    }
  };

  const handleRemove = () => {
    setValue(null);
  };

  return (
    <FormControl fullWidth error={touched && !!error}>
      {label && <Typography variant="body2" gutterBottom>{label}</Typography>}
      {!value && (
        <ButtonFilePicker
          accept=".gpx,.gxp"
          onSelect={handleFileSelect}
          disabled={disabled}
          ButtonSlimProps={{
            className: classes.uploadButton,
          }}
        >
          Pasirinkti GXP failą
        </ButtonFilePicker>
      )}
      {value && (
        <div className={classes.fileContainer}>
          <Box className={classes.fileDisplay}>
            <InsertDriveFileIcon className={classes.fileIcon} />
            <Box className={classes.fileInfo}>
              <Typography variant="body1" className={classes.fileName}>
                {value.name || (value.id ? 'GXP failas' : 'Pasirinktas failas')}
              </Typography>
              {value.size && (
                <Typography variant="body2" className={classes.fileSize}>
                  {value.size > 1024 
                    ? `${(value.size / 1024).toFixed(2)} KB`
                    : `${value.size} B`}
                </Typography>
              )}
            </Box>
          </Box>
          <Paper className={classes.fileActions}>
            <ButtonSlim
              startIcon={<RemoveIcon />}
              disabled={disabled}
              onClick={handleRemove}
            >
              Pašalinti
            </ButtonSlim>
          </Paper>
        </div>
      )}
      {touched && error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

GXPFileField.defaultProps = {
  disabled: false,
  label: 'Maršruto GXP failas',
};

GXPFileField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
};

export default GXPFileField;
